import { CASCADING_SELECT_CF_TYPE } from '@atlassian/jira-platform-field-config';
import { useCascadingSelectField } from '../../../cascading-select';
import {
	assertClipboardDataIsCompatible,
	assertClipboardHasRichFormat,
	useGenericClipboardHandler,
} from '../../../clipboard-handler/utils';
import type {
	ClipboardItem,
	ListIssueFieldTransformed,
	PasteHandler,
	PasteMetadata,
} from '../../../types';
import { createClipboardHelperComponent } from '../../utils';
import type { CascadingSelectFieldValue, TransformedCascadingSelectField } from '../types';

const isCascadingSelectOption = (fieldValue: unknown): fieldValue is CascadingSelectFieldValue =>
	fieldValue != null && typeof fieldValue === 'object' && 'parentOptionValue' in fieldValue;

const getPastedValue = (
	clipboardItem: ClipboardItem,
	metadata: PasteMetadata | null,
): CascadingSelectFieldValue | null => {
	if (
		!assertClipboardHasRichFormat(clipboardItem) ||
		!assertClipboardDataIsCompatible(metadata, [CASCADING_SELECT_CF_TYPE])
	) {
		return null;
	}

	if (!isCascadingSelectOption(metadata.fieldValue)) {
		return null;
	}

	const { parentOptionValue, childOptionValue } = metadata.fieldValue;

	return { parentOptionValue, childOptionValue };
};

const useCascadingSelectClipboardHandler: PasteHandler = (
	issue: { key: string },
	field: ListIssueFieldTransformed,
) => {
	const [, { saveValue }] = useCascadingSelectField({
		// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
		fieldAriId: (field as TransformedCascadingSelectField).id,
		issueKey: issue.key,
		fieldKey: field?.fieldId,
		initialValue: isCascadingSelectOption(field.value) ? field.value : null,
	});

	return useGenericClipboardHandler(field, getPastedValue, saveValue);
};

const CascadingSelectClipboardHelper = createClipboardHelperComponent(
	useCascadingSelectClipboardHandler,
);

export const cascadingSelectClipboardHandler = {
	[CASCADING_SELECT_CF_TYPE]: CascadingSelectClipboardHelper,
};
